import { z } from "zod";
import {
  ACCOUNT_REQUIRED,
  AI_TOOL_REQUIRED,
  CHANGE_PASSWORD_INVALID,
  CONTACT_PATH_ETC_REQUIRED,
  CONTACT_PATH_REQUIRED,
  EMAIL_CHECK_REQUIRED,
  EMAIL_FORMAT,
  EMAIL_REQUIRED,
  NAME_REQUIRED,
  NEW_PASSWORD_REQUIRED,
  OTHER_CONTEST_NAME_REQUIRED,
  PASSWORD_CONFIRM_INVALID,
  PASSWORD_CONFIRM_REQUIRED,
  PASSWORD_FORMAT,
  PASSWORD_REQUIRED,
  PHONE_FORMAT,
  PHONE_REQUIRED,
  PRIVACY_POLICY_REQUIRED,
  SNS_LINK_REQUIRED,
  USE_TERM_REQUIRED,
  VIDEO_LINK_REQUIRED,
} from "@/constants/inputErrorMessage";

const PHONE_REGEX = /[0-9]/g;
const PASSWORD_REGEX = /^(?=.*[a-zA-Z])(?=.*[0-9]).{8,15}$/;

export const EMAIL_REGEX =
  "^[a-zA-Z0-9+-\\_.]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$";

const BIRTH_FORMAT_REGEX = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD 형식의 정규식
const BIRTH_VALID_REGEX =
  /^(19[0-9][0-9]|20\d{2})-(0[0-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$/; // YYYY-MM-DD 각 자리에 유효한 생년월일인지 확인

// NOTE: 로그인
export const zodLogin = z.object({
  user_email: z
    .string()
    .min(1, EMAIL_REQUIRED)
    .email({ message: EMAIL_FORMAT }),
  user_password: z
    .string()
    .min(1, PASSWORD_REQUIRED)
    .regex(PASSWORD_REGEX, { message: PASSWORD_FORMAT }),
  auto_login: z.boolean(),
});

// NOTE: 회원가입
export const zodJoin = z
  .object({
    user_email: z
      .string()
      .min(1, EMAIL_REQUIRED)
      .email({ message: EMAIL_FORMAT }),
    user_password: z
      .string()
      .min(1, { message: PASSWORD_REQUIRED })
      .regex(PASSWORD_REGEX, {
        message: PASSWORD_FORMAT,
      }),
    confirm_password: z
      .string()
      .min(1, { message: PASSWORD_CONFIRM_REQUIRED })
      .regex(PASSWORD_REGEX, {
        message: PASSWORD_FORMAT,
      }),
    user_name: z.string().min(1, { message: NAME_REQUIRED }),
    user_birth: z.optional(
      z
        .string()
        .regex(BIRTH_FORMAT_REGEX, {
          message: "연월일 총 8자리를 입력해주세요.",
        })
        .regex(BIRTH_VALID_REGEX, {
          message: "유효한 생년월일을 입력해주세요.",
        }),
    ),
    user_phone: z
      .string()
      .min(1, { message: PHONE_REQUIRED })
      .regex(PHONE_REGEX, { message: PHONE_FORMAT }),
    user_seq: z.optional(z.string()),
    use_term: z.boolean().refine(value => value === true, {
      message: USE_TERM_REQUIRED,
    }),
    privacy_policy: z.boolean().refine(value => value === true, {
      message: PRIVACY_POLICY_REQUIRED,
    }),

    user_email_checked: z.boolean().refine(value => value === true, {
      message: EMAIL_CHECK_REQUIRED,
    }),

    email_receive_yn: z.boolean().optional(),
  })
  .refine(data => data.user_password === data.confirm_password, {
    message: PASSWORD_CONFIRM_INVALID,
    path: ["confirm_password"],
  });
// .refine(data => data.user_email_checked === true, {
//   message: EMAIL_CHECK_REQUIRED,
//   path: ["user_email"],
// });

// NOTE: 계정 찾기
export const zodFindAccount = z.object({
  user_phone: z
    .string()
    .min(1, { message: PHONE_REQUIRED })
    .regex(PHONE_REGEX, { message: PHONE_FORMAT }),
});

// NOTE: 비밀번호 변경하기 - 계정 찾기
export const zodChangePassword = z
  .object({
    new_password: z
      .string()
      .min(1, { message: PASSWORD_REQUIRED })
      .regex(PASSWORD_REGEX, {
        message: PASSWORD_FORMAT,
      }),
    confirm_password: z
      .string()
      .min(1, { message: PASSWORD_CONFIRM_REQUIRED })
      .regex(PASSWORD_REGEX, {
        message: PASSWORD_FORMAT,
      }),
  })
  .refine(data => data.new_password === data.confirm_password, {
    message: PASSWORD_CONFIRM_INVALID,
    path: ["confirm_password"],
  });

// NOTE: 계정 변경하기 - 마이페이지
export const zodChangeAccount = z.object({
  user_name: z.string().min(1, { message: NAME_REQUIRED }),
  user_birth: z.string(),
  user_phone: z
    .string()
    .min(1, { message: PHONE_REQUIRED })
    .regex(PHONE_REGEX, { message: PHONE_FORMAT }),
  user_seq: z.optional(z.string()),
  email_receive_yn: z.boolean(),
  phone_changed: z.optional(z.boolean()),
});

// NOTE: 비밀번호 변경하기 - 마이페이지
export const zodChangeMyPassword = z
  .object({
    user_password: z
      .string()
      .min(1, { message: PASSWORD_REQUIRED })
      .regex(PASSWORD_REGEX, {
        message: PASSWORD_FORMAT,
      }),
    new_password: z
      .string()
      .min(1, { message: NEW_PASSWORD_REQUIRED })
      .regex(PASSWORD_REGEX, {
        message: PASSWORD_FORMAT,
      }),
    confirm_password: z
      .string()
      .min(1, { message: PASSWORD_CONFIRM_REQUIRED })
      .regex(PASSWORD_REGEX, {
        message: PASSWORD_FORMAT,
      }),
  })
  .refine(data => data.new_password === data.confirm_password, {
    message: CHANGE_PASSWORD_INVALID,
    path: ["confirm_password"],
  });

export const zodContestSubmit = z
  .object({
    participant_email: z
      .string()
      .min(1, EMAIL_REQUIRED)
      .email({ message: EMAIL_FORMAT }),
    participant_name: z.string().min(1, { message: NAME_REQUIRED }),
    participant_account: z.string().min(1, { message: ACCOUNT_REQUIRED }),
    // SNS 구독(예)인 경우 링크 필수
    sns_subscribe_yn: z.boolean(),
    sns_link: z.optional(z.string()),
    contact_path_etc: z.optional(z.string()),
    video_link: z.string().min(1, { message: VIDEO_LINK_REQUIRED }),
    video_description: z.optional(z.string()),
    participant_phone: z
      .string()
      .min(1, { message: PHONE_REQUIRED })
      .regex(PHONE_REGEX, { message: PHONE_FORMAT }),
    privacy_policy: z.boolean().refine(value => value === true, {
      message: PRIVACY_POLICY_REQUIRED,
    }),
    contact_path: z.string().min(1, { message: CONTACT_PATH_REQUIRED }),
    // AI 활용(예)인 경우 입력 필드 필수
    is_ai: z.boolean(),
    ai_tool: z.optional(z.string()),
    is_submit_other_contest: z.boolean(),
    other_contest_name: z.optional(z.string()),
    is_agree_other_contest: z.boolean(),
    is_agree_copyright: z.boolean(),
  })
  .superRefine((data, ctx) => {
    // SNS 구독 = 예 -> SNS 링크 필수
    if (data.sns_subscribe_yn === true) {
      if (!data.sns_link || data.sns_link.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sns_link"],
          message: SNS_LINK_REQUIRED,
        });
      }
    }

    // 참가경로 = 기타 -> 기타 내용 필수
    if (data.contact_path === "기타") {
      if (!data.contact_path_etc || data.contact_path_etc.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["contact_path_etc"],
          message: CONTACT_PATH_ETC_REQUIRED,
        });
      }
    }

    // AI 활용 = 예 -> 사용한 AI 입력 필수
    if (data.is_ai === true) {
      if (!data.ai_tool || data.ai_tool.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ai_tool"],
          message: AI_TOOL_REQUIRED,
        });
      }
    }
  })
  .refine(
    data => {
      if (data.is_submit_other_contest === true) {
        return (
          data.other_contest_name && data.other_contest_name.trim().length > 0
        );
      }
      return true;
    },
    {
      message: OTHER_CONTEST_NAME_REQUIRED,
      path: ["other_contest_name"],
    },
  );
